import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { lectureAPI, studyPlanAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
    Upload,
    FileText,
    Image,
    Loader2,
    CheckCircle,
    Trash2,
    Play,
    FileQuestion,
    X,
    BookOpen,
    Lightbulb,
    ChevronDown,
    ChevronRight,
    Link2,
    FileSearch,
    GraduationCap
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ScrollArea } from './ui/scroll-area';

import { SUBJECTS } from '../lib/utils';

export default function LectureUpload({ onSessionStart }) {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadQueue, setUploadQueue] = useState([]); // [{file, progress, status, id}]
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [subject, setSubject] = useState('');
    const [numQuestions, setNumQuestions] = useState(5);
    const [studyPlans, setStudyPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    
    // Practice session dialog state
    const [showPracticeDialog, setShowPracticeDialog] = useState(false);
    const [selectedUpload, setSelectedUpload] = useState(null);
    const [practiceQuestionCount, setPracticeQuestionCount] = useState('10');
    const [startingSession, setStartingSession] = useState(false);

    // Concepts modal state
    const [showConcepts, setShowConcepts] = useState(false);
    const [conceptsData, setConceptsData] = useState(null);
    const [loadingConcepts, setLoadingConcepts] = useState(false);
    const [expandedConcepts, setExpandedConcepts] = useState({});

    // Summary modal state
    const [showSummary, setShowSummary] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Multi-select state
    const [selectedUploads, setSelectedUploads] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    useEffect(() => {
        fetchUploads();
        fetchStudyPlans();
    }, []);

    const fetchStudyPlans = async () => {
        try {
            const response = await studyPlanAPI.getAll();
            setStudyPlans(response.data.plans || []);
        } catch (err) {
            console.error('Failed to fetch study plans:', err);
        }
    };

    const fetchUploads = async () => {
        try {
            const response = await lectureAPI.getUploads();
            setUploads(response.data.uploads || []);
        } catch (err) {
            console.error('Failed to fetch uploads:', err);
        } finally {
            setLoading(false);
        }
    };

    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.docx', '.doc', '.pptx', '.ppt', '.xlsx'];
    const allowedTypes = [
        'application/pdf',
        'image/png', 'image/jpeg', 'image/jpg', 'image/webp',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validateFile = (file) => {
        const fileName = file.name.toLowerCase();
        const fileExt = '.' + fileName.split('.').pop();
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
            toast.error(`${file.name}: Invalid file type.`);
            return false;
        }
        if (file.size > 150 * 1024 * 1024) {
            toast.error(`${file.name}: File too large (max 150MB).`);
            return false;
        }
        return true;
    };

    const addFiles = (fileList) => {
        const validFiles = Array.from(fileList).filter(validateFile);
        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files?.length) addFiles(e.target.files);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    };

    const removeSelectedFile = (idx) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            toast.error('Please select at least one file');
            return;
        }

        setUploading(true);
        const queue = selectedFiles.map((file, i) => ({
            file, progress: 0, status: 'pending', id: `q-${i}-${Date.now()}`,
        }));
        setUploadQueue(queue);

        for (let i = 0; i < queue.length; i++) {
            setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, status: 'uploading' } : q));

            try {
                const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
                const file = queue[i].file;
                let uploadId;

                if (file.size > CHUNK_SIZE) {
                    // Chunked upload for files > 4MB
                    const planId = selectedPlanId && selectedPlanId !== 'none' ? selectedPlanId : null;
                    const initRes = await lectureAPI.initUpload(file.name, file.size, subject, planId, numQuestions);
                    const sessionId = initRes.data.upload_session_id;
                    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

                    for (let c = 0; c < totalChunks; c++) {
                        const start = c * CHUNK_SIZE;
                        const end = Math.min(start + CHUNK_SIZE, file.size);
                        const chunk = file.slice(start, end);
                        const fd = new FormData();
                        fd.append('file', chunk, `chunk_${c}`);
                        await lectureAPI.uploadChunk(sessionId, c, fd);
                        const pct = Math.round(((c + 1) / totalChunks) * 45);
                        setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: pct } : q));
                    }

                    setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: 48, status: 'processing' } : q));
                    const completeRes = await lectureAPI.completeUpload(sessionId);
                    uploadId = completeRes.data.upload_id;
                } else {
                    // Small file — single request
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('subject', subject || '');
                    formData.append('num_questions', numQuestions.toString());
                    if (selectedPlanId && selectedPlanId !== 'none') formData.append('plan_id', selectedPlanId);

                    const response = await lectureAPI.uploadWithProgress(formData, (progressEvent) => {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: Math.min(pct, 49) } : q));
                    });
                    uploadId = response.data.upload_id;
                }
                setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: 50, status: 'processing' } : q));

                // Poll for completion
                let pollCount = 0;
                await new Promise((resolve) => {
                    const poll = setInterval(async () => {
                        try {
                            const sr = await lectureAPI.getStatus(uploadId);
                            if (sr.data.status === 'completed') {
                                clearInterval(poll);
                                setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: 100, status: 'completed' } : q));
                                toast.success(`${queue[i].file.name}: ${sr.data.questions_generated} questions & ${sr.data.concepts_extracted || 0} concepts`);
                                resolve();
                            } else if (sr.data.status === 'failed') {
                                clearInterval(poll);
                                setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, status: 'failed' } : q));
                                toast.error(`${queue[i].file.name}: Generation failed`);
                                resolve();
                            } else {
                                pollCount++;
                                const pct = Math.min(50 + Math.round((pollCount / 60) * 49), 99);
                                setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, progress: pct } : q));
                                if (pollCount >= 60) { clearInterval(poll); resolve(); }
                            }
                        } catch (pollErr) {
                            console.error('Poll error:', pollErr);
                            pollCount++;
                            if (pollCount >= 60) { clearInterval(poll); resolve(); }
                        }
                    }, 2000);
                });
            } catch (err) {
                const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
                setUploadQueue(prev => prev.map((q, idx) => idx === i ? { ...q, status: 'failed' } : q));
                toast.error(`${queue[i].file.name}: Upload failed — ${detail}`);
            }
        }

        setUploading(false);
        setSelectedFiles([]);
        setUploadQueue([]);
        fetchUploads();
    };

    const handleDelete = async (uploadId) => {
        if (!window.confirm('Delete this upload and its questions?')) return;

        try {
            await lectureAPI.deleteUpload(uploadId);
            toast.success('Upload deleted');
            setSelectedUploads(prev => { const s = new Set(prev); s.delete(uploadId); return s; });
            fetchUploads();
        } catch (err) {
            toast.error('Failed to delete upload');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUploads.size === 0) return;
        if (!window.confirm(`Delete ${selectedUploads.size} upload${selectedUploads.size > 1 ? 's' : ''} and all their questions?`)) return;
        setBulkDeleting(true);
        let deleted = 0;
        for (const id of selectedUploads) {
            try {
                await lectureAPI.deleteUpload(id);
                deleted++;
            } catch { /* continue */ }
        }
        toast.success(`Deleted ${deleted} upload${deleted > 1 ? 's' : ''}`);
        setSelectedUploads(new Set());
        setBulkDeleting(false);
        fetchUploads();
    };

    const toggleSelectUpload = (id) => {
        setSelectedUploads(prev => {
            const s = new Set(prev);
            s.has(id) ? s.delete(id) : s.add(id);
            return s;
        });
    };

    const toggleSelectAll = () => {
        if (selectedUploads.size === uploads.length) {
            setSelectedUploads(new Set());
        } else {
            setSelectedUploads(new Set(uploads.map(u => u.id)));
        }
    };

    const openPracticeDialog = (upload) => {
        setSelectedUpload(upload);
        // Default to all available questions or 10, whichever is smaller
        const defaultCount = Math.min(upload.questions_generated || 10, 10);
        setPracticeQuestionCount(defaultCount.toString());
        setShowPracticeDialog(true);
    };

    const openConcepts = async (upload) => {
        setShowConcepts(true);
        setConceptsData(null);
        setExpandedConcepts({});
        setLoadingConcepts(true);
        try {
            const res = await lectureAPI.getConcepts(upload.id);
            setConceptsData({ ...res.data, filename: upload.filename, subject: upload.subject });
        } catch {
            toast.error('Failed to load concepts');
            setShowConcepts(false);
        } finally {
            setLoadingConcepts(false);
        }
    };

    const openSummary = async (upload) => {
        setShowSummary(true);
        setSummaryData(null);
        setLoadingSummary(true);
        try {
            const res = await lectureAPI.getSummary(upload.id);
            setSummaryData({ summary: res.data.summary, filename: upload.filename, subject: upload.subject });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate summary');
            setShowSummary(false);
        } finally {
            setLoadingSummary(false);
        }
    };

    const toggleConcept = (idx) => {
        setExpandedConcepts(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleStartSession = async () => {
        if (!selectedUpload) return;
        
        setStartingSession(true);
        try {
            const questionCount = parseInt(practiceQuestionCount);
            const response = await lectureAPI.startSession(selectedUpload.id, questionCount);
            setShowPracticeDialog(false);
            
            if (onSessionStart) {
                onSessionStart(response.data);
            } else {
                navigate(`/lecture-practice/${response.data.session_id}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start session');
        } finally {
            setStartingSession(false);
        }
    };

    const getFileIcon = (contentTypeOrName) => {
        const str = (contentTypeOrName || '').toLowerCase();
        
        // Check by file extension in name
        if (str.endsWith('.pdf') || str === 'application/pdf') {
            return <FileText className="h-5 w-5 text-red-500" />;
        }
        if (str.endsWith('.docx') || str.endsWith('.doc') || str.includes('wordprocessingml') || str.includes('msword')) {
            return <FileText className="h-5 w-5 text-blue-600" />;
        }
        if (str.endsWith('.pptx') || str.endsWith('.ppt') || str.includes('presentationml') || str.includes('ms-powerpoint')) {
            return <FileText className="h-5 w-5 text-orange-500" />;
        }
        if (str.endsWith('.xlsx') || str.includes('spreadsheetml')) {
            return <FileText className="h-5 w-5 text-green-600" />;
        }
        // Default to image icon
        return <Image className="h-5 w-5 text-blue-500" />;
    };

    return (
        <div className="space-y-6">
            {/* Upload Section */}
            <Card className="border-2 border-dashed border-indigo-200 bg-indigo-50/30">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Upload className="h-5 w-5 text-indigo-600" />
                        Upload Lecture Slides
                    </CardTitle>
                    <CardDescription>
                        Upload PDF or images of your lecture slides and we'll generate practice questions from them
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* File Input */}
                    <div 
                        className={cn(
                            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                            isDragging ? "border-indigo-500 bg-indigo-100/70 scale-[1.01]" :
                            selectedFiles.length > 0 ? "border-indigo-400 bg-indigo-100/50" : "border-slate-300 hover:border-indigo-400"
                        )}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        data-testid="upload-dropzone"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.pptx,.ppt,.xlsx"
                            onChange={handleFileSelect}
                            className="hidden"
                            multiple
                        />
                        {selectedFiles.length > 0 ? (
                            <div className="space-y-2">
                                {selectedFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-center gap-3">
                                        {getFileIcon(file.type || file.name)}
                                        <span className="font-medium text-slate-700 text-sm">{file.name}</span>
                                        <span className="text-xs text-slate-400">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); removeSelectedFile(idx); }}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                                <p className="text-xs text-indigo-500 mt-1">Click or drop to add more files</p>
                            </div>
                        ) : (
                            <>
                                <Upload className={cn("h-10 w-10 mx-auto mb-2", isDragging ? "text-indigo-500" : "text-slate-400")} />
                                <p className={isDragging ? "text-indigo-600 font-medium" : "text-slate-600"}>
                                    {isDragging ? "Drop files here" : "Click to upload or drag and drop"}
                                </p>
                                <p className="text-sm text-slate-400 mt-1">PDF, Images, Word, PowerPoint, Excel (up to 150MB) — multiple files supported</p>
                            </>
                        )}
                    </div>

                    {/* Options */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label htmlFor="subject">Subject (Optional)</Label>
                            <Select value={subject} onValueChange={setSubject}>
                                <SelectTrigger data-testid="lecture-subject-select">
                                    <SelectValue placeholder="Select subject" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SUBJECTS.map(s => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="numQuestions">Number of Questions</Label>
                            <Select value={numQuestions.toString()} onValueChange={(v) => setNumQuestions(parseInt(v))}>
                                <SelectTrigger data-testid="lecture-num-questions-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[3, 5, 10, 15, 20].map(n => (
                                        <SelectItem key={n} value={n.toString()}>{n} questions</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="studyPlan">Link to Study Plan (Optional)</Label>
                            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                                <SelectTrigger data-testid="lecture-plan-select">
                                    <SelectValue placeholder="Select study plan" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No plan</SelectItem>
                                    {studyPlans.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Upload Queue */}
                    {uploading && uploadQueue.length > 0 && (
                        <div className="space-y-2 rounded-lg border border-slate-200 p-3 bg-slate-50" data-testid="upload-queue">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-slate-500">
                                    Processing {uploadQueue.filter(q => q.status === 'completed').length} of {uploadQueue.length} files (one at a time)
                                </span>
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
                            </div>
                            {uploadQueue.map((item, idx) => (
                                <div key={item.id} className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm">
                                        {item.status === 'completed' ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" /> :
                                         item.status === 'failed' ? <X className="h-3.5 w-3.5 text-red-500 flex-shrink-0" /> :
                                         item.status === 'pending' ? <div className="h-3.5 w-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" /> :
                                         <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500 flex-shrink-0" />}
                                        <span className={cn("truncate flex-1", item.status === 'completed' ? "text-slate-400 line-through" : "text-slate-700")}>{item.file.name}</span>
                                        <span className={cn("text-xs font-semibold flex-shrink-0",
                                            item.status === 'completed' ? "text-emerald-600" :
                                            item.status === 'failed' ? "text-red-600" :
                                            item.status === 'pending' ? "text-slate-400" :
                                            "text-indigo-600"
                                        )} data-testid={`upload-percent-${idx}`}>
                                            {item.status === 'completed' ? 'Done' :
                                             item.status === 'failed' ? 'Failed' :
                                             item.status === 'pending' ? 'Queued' :
                                             `${item.progress}%`}
                                        </span>
                                    </div>
                                    {(item.status === 'uploading' || item.status === 'processing') && (
                                        <Progress value={item.progress} className="h-1" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload Button */}
                    <Button 
                        onClick={handleUpload} 
                        disabled={selectedFiles.length === 0 || uploading}
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        data-testid="upload-btn"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing {uploadQueue.filter(q => q.status === 'completed').length}/{uploadQueue.length} files...
                            </>
                        ) : (
                            <>
                                <Upload className="mr-2 h-4 w-4" />
                                Upload {selectedFiles.length > 0 ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}` : '& Generate Questions'}
                            </>
                        )}
                    </Button>
                </CardContent>
            </Card>

            {/* Previous Uploads */}
            {uploads.length > 0 && (
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                            Your Lecture Uploads
                        </CardTitle>
                        <div className="flex items-center gap-3">
                            {selectedUploads.size > 0 && (
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleting}
                                    data-testid="bulk-delete-btn"
                                >
                                    {bulkDeleting ? (
                                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                    )}
                                    Delete {selectedUploads.size} selected
                                </Button>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="select-all-checkbox">
                                <input
                                    type="checkbox"
                                    checked={uploads.length > 0 && selectedUploads.size === uploads.length}
                                    onChange={toggleSelectAll}
                                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                                <span className="text-xs text-slate-500 font-medium">Select all</span>
                            </label>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {uploads.map((upload) => (
                                <div 
                                    key={upload.id}
                                    className={cn(
                                        "flex items-center justify-between p-3 rounded-lg border bg-slate-50 transition-colors",
                                        selectedUploads.has(upload.id) ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedUploads.has(upload.id)}
                                            onChange={() => toggleSelectUpload(upload.id)}
                                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                                            data-testid={`select-upload-${upload.id}`}
                                        />
                                        {getFileIcon(upload.content_type)}
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-slate-800 text-sm">
                                                {upload.filename}
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                                                {upload.subject && (
                                                    <Badge variant="outline" className="text-xs">
                                                        {upload.subject}
                                                    </Badge>
                                                )}
                                                <span>{upload.questions_generated || 0} questions</span>
                                                {upload.concepts_extracted > 0 && (
                                                    <><span>•</span><span>{upload.concepts_extracted} concepts</span></>
                                                )}
                                                <span>•</span>
                                                <span>{new Date(upload.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <div className="mt-1.5">
                                                <Select
                                                    value={upload.plan_id || 'none'}
                                                    onValueChange={async (val) => {
                                                        const planId = val === 'none' ? null : val;
                                                        try {
                                                            await lectureAPI.linkPlan(upload.id, planId);
                                                            setUploads(prev => prev.map(u => u.id === upload.id ? { ...u, plan_id: planId } : u));
                                                            const planName = studyPlans.find(p => p.id === planId)?.name;
                                                            toast.success(planId ? `Linked to ${planName}` : 'Unlinked from study plan');
                                                        } catch { toast.error('Failed to update plan link'); }
                                                    }}
                                                >
                                                    <SelectTrigger className="h-7 text-xs w-[180px]" data-testid={`link-plan-${upload.id}`}>
                                                        <SelectValue placeholder="Link to plan..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">No study plan</SelectItem>
                                                        {studyPlans.map(p => (
                                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {upload.status === 'completed' && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                                                onClick={() => openSummary(upload)}
                                                data-testid={`view-summary-btn-${upload.id}`}
                                            >
                                                <FileSearch className="mr-1 h-3 w-3" />
                                                Summary
                                            </Button>
                                        )}
                                        {upload.status === 'completed' && upload.concepts_extracted > 0 && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                                onClick={() => navigate(`/lecture-learn/${upload.id}`)}
                                                data-testid={`learn-btn-${upload.id}`}
                                            >
                                                <GraduationCap className="mr-1 h-3 w-3" />
                                                Learn
                                            </Button>
                                        )}
                                        {upload.status === 'completed' && upload.concepts_extracted > 0 && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                                                onClick={() => openConcepts(upload)}
                                                data-testid={`view-concepts-btn-${upload.id}`}
                                            >
                                                <Lightbulb className="mr-1 h-3 w-3" />
                                                Concepts
                                            </Button>
                                        )}
                                        {upload.status === 'completed' && (
                                            <Button
                                                size="sm"
                                                onClick={() => openPracticeDialog(upload)}
                                            >
                                                <Play className="mr-1 h-3 w-3" />
                                                Practice
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(upload.id)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Practice Session Dialog */}
            <Dialog open={showPracticeDialog} onOpenChange={setShowPracticeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope' }}>
                            Start Practice Session
                        </DialogTitle>
                        <DialogDescription>
                            {selectedUpload && (
                                <>Practice questions from <strong>{selectedUpload.filename}</strong></>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label>Number of Questions</Label>
                            <Select value={practiceQuestionCount} onValueChange={setPracticeQuestionCount}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[5, 10, 15, 20, 25, 30, 40, 50, 75, 100].map(n => {
                                        const available = selectedUpload?.questions_generated || 0;
                                        const disabled = n > available;
                                        return (
                                            <SelectItem 
                                                key={n} 
                                                value={n.toString()}
                                                disabled={disabled}
                                            >
                                                {n} questions {disabled && `(only ${available} available)`}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                            {selectedUpload && (
                                <p className="text-sm text-slate-500">
                                    {selectedUpload.questions_generated} questions available from this upload
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPracticeDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleStartSession} disabled={startingSession}>
                            {startingSession ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Starting...
                                </>
                            ) : (
                                <>
                                    <Play className="mr-2 h-4 w-4" />
                                    Start Practice
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Concepts Modal */}
            <Dialog open={showConcepts} onOpenChange={setShowConcepts}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="concepts-modal">
                    <DialogHeader className="px-6 pt-6 pb-3">
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                            <Lightbulb className="h-5 w-5 text-amber-500" />
                            Extracted Concepts
                        </DialogTitle>
                        {conceptsData && (
                            <DialogDescription>
                                {conceptsData.filename} — {conceptsData.concept_count} concept{conceptsData.concept_count !== 1 ? 's' : ''}
                                {conceptsData.subject && ` • ${conceptsData.subject}`}
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                        {loadingConcepts ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
                                <span className="ml-2 text-slate-500">Loading concepts...</span>
                            </div>
                        ) : conceptsData?.concepts?.length > 0 ? (
                            <div className="space-y-2">
                                {conceptsData.concepts.map((concept, idx) => {
                                    const expanded = expandedConcepts[idx];
                                    return (
                                        <div key={idx} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`concept-${idx}`}>
                                            <button
                                                onClick={() => toggleConcept(idx)}
                                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                                            >
                                                {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-slate-800 text-sm">{concept.topic}</p>
                                                    {!expanded && <p className="text-xs text-slate-400 truncate">{concept.definition}</p>}
                                                </div>
                                                <Badge variant="outline" className="text-xs flex-shrink-0">
                                                    {concept.key_points?.length || 0} points
                                                </Badge>
                                            </button>
                                            {expanded && (
                                                <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50 space-y-3">
                                                    <div>
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Definition</p>
                                                        <p className="text-sm text-slate-700 leading-relaxed">{concept.definition}</p>
                                                    </div>
                                                    {concept.key_points?.length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Key Points</p>
                                                            <ul className="space-y-1">
                                                                {concept.key_points.map((pt, i) => (
                                                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0"></span>
                                                                        {pt}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {concept.relationships?.length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Related Topics</p>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {concept.relationships.map((rel, i) => (
                                                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs border border-indigo-100">
                                                                        <Link2 className="h-3 w-3" />{rel}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <Lightbulb className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                                <p className="text-slate-500">No concepts extracted for this upload.</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Summary Modal */}
            <Dialog open={showSummary} onOpenChange={setShowSummary}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="summary-modal">
                    <DialogHeader className="px-6 pt-6 pb-3">
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                            <FileSearch className="h-5 w-5 text-cyan-500" />
                            File Summary
                        </DialogTitle>
                        {summaryData && (
                            <DialogDescription>
                                {summaryData.filename}
                                {summaryData.subject && ` — ${summaryData.subject}`}
                            </DialogDescription>
                        )}
                    </DialogHeader>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                        {loadingSummary ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                                <span className="text-slate-500">Generating summary...</span>
                                <span className="text-xs text-slate-400">This may take a few seconds</span>
                            </div>
                        ) : summaryData?.summary ? (
                            <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-slate-800 prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-900" data-testid="summary-content">
                                <SummaryMarkdown text={summaryData.summary} />
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FileSearch className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                                <p className="text-slate-500">No summary available.</p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** Minimal markdown renderer for summary content */
function SummaryMarkdown({ text }) {
    const lines = text.split('\n');
    const elements = [];
    let listItems = [];

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(<ul key={`ul-${elements.length}`} className="space-y-1 my-2">{listItems}</ul>);
            listItems = [];
        }
    };

    lines.forEach((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) { flushList(); return; }

        // Headers
        if (trimmed.startsWith('## ')) {
            flushList();
            elements.push(<h2 key={i} className="text-base font-semibold text-slate-800 mt-4 mb-1">{trimmed.slice(3)}</h2>);
            return;
        }
        if (trimmed.startsWith('### ')) {
            flushList();
            elements.push(<h3 key={i} className="text-sm font-semibold text-slate-700 mt-3 mb-1">{trimmed.slice(4)}</h3>);
            return;
        }

        // Bullet points
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            const content = trimmed.slice(2);
            listItems.push(
                <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0"></span>
                    <span dangerouslySetInnerHTML={{ __html: boldify(content) }} />
                </li>
            );
            return;
        }

        // Regular paragraph
        flushList();
        elements.push(<p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: boldify(trimmed) }} />);
    });
    flushList();

    return <>{elements}</>;
}

function boldify(text) {
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}
